import {useCallback, useEffect, useMemo, useState} from "react";
import {Link, useParams} from "react-router-dom";
import {
    addEmployee,
    getBusinessById,
    getEmployeeByBusiness,
    getPayoutsByBusiness,
    getTipsByBusiness,
} from "../../services/firestoreService";
import {getEmployeeReadinessIssue, onboardingStatusLabel} from "../../lib/onboarding";
import {reviewBusinessOnboarding} from "../../services/onboardingService";

const formatTimestamp = (value) => {
    if (!value) return "—";
    if (typeof value.toDate === "function") return value.toDate().toLocaleString();
    if (value.seconds) return new Date(value.seconds * 1000).toLocaleString();
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? "—" : parsed.toLocaleString();
};

function AdminBusinessDashboard() {
    const {businessID} = useParams();
    const [business, setBusiness] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [tips, setTips] = useState([]);
    const [payouts, setPayouts] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newEmployeeName, setNewEmployeeName] = useState("");
    const [reviewNote, setReviewNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState("");

    const fetchData = useCallback(async () => {
        const [businessData, employeeData, tipsData, payoutsData] = await Promise.all([
            getBusinessById(businessID),
            getEmployeeByBusiness(businessID),
            getTipsByBusiness(businessID),
            getPayoutsByBusiness(businessID),
        ]);
        setBusiness(businessData);
        setEmployees(employeeData || []);
        setTips(tipsData || []);
        setPayouts(payoutsData || []);
    }, [businessID]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const readyEmployees = useMemo(() => employees.filter(
        (employee) => !["deactivated", "readonly"].includes(employee.accountStatus) && !getEmployeeReadinessIssue(employee, business),
    ), [business, employees]);

    if (!business) return <p className="p-8 text-gray-500">Loading business…</p>;

    const handleAddEmployee = async (event) => {
        event.preventDefault();
        if (!newEmployeeName.trim()) return;
        setBusy(true);
        setMessage("");
        try {
            await addEmployee(businessID, {displayName: newEmployeeName.trim()});
            setIsModalOpen(false);
            setNewEmployeeName("");
            await fetchData();
        } catch (error) {
            setMessage(error.message);
        } finally {
            setBusy(false);
        }
    };

    const handleReview = async (decision) => {
        if (["REQUEST_CHANGES", "REJECT"].includes(decision) && !reviewNote.trim()) {
            setMessage("Add a note for the owner before choosing that decision.");
            return;
        }
        setBusy(true);
        setMessage("");
        try {
            const result = await reviewBusinessOnboarding(businessID, decision, reviewNote.trim());
            setMessage(`Review saved: ${onboardingStatusLabel(result.onboardingStatus)}.`);
            setReviewNote("");
            await fetchData();
        } catch (error) {
            setMessage(error.message);
        } finally {
            setBusy(false);
        }
    };

    const employeeName = (employeeID) => {
        const employee = employees.find((item) => item.id === employeeID);
        return employee?.displayName || employee?.name || "Unknown";
    };

    return (
        <main className="min-h-screen bg-gray-50 p-4 sm:p-8">
            <div className="mx-auto max-w-6xl space-y-6">
                <Link to="/admin/business" className="text-sm font-semibold text-[#0d7c5f]">← Onboarding queue</Link>
                <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                    <div className="flex flex-col justify-between gap-4 sm:flex-row">
                        <div>
                            <p className="text-xs font-bold uppercase tracking-wider text-gray-500">{businessID}</p>
                            <h1 className="mt-1 text-3xl font-bold text-gray-900">{business.displayName || business.name || business.legalName || "Business"}</h1>
                            <p className="mt-2 text-sm text-gray-600">{business.legalName || "No legal name"} · {business.repEmail || business.ownerEmail || "No owner email"}</p>
                            <p className="mt-1 text-sm text-gray-500">{business.address || "No address"} {business.countryCode ? `· ${business.countryCode}` : ""}</p>
                        </div>
                        <div className="text-sm text-gray-500 sm:text-right">
                            <span className="inline-flex rounded-full bg-gray-100 px-3 py-1 font-bold text-gray-700">{business.onboardingStatus ? onboardingStatusLabel(business.onboardingStatus) : "Legacy business"}</span>
                            <p className="mt-3">Created: {formatTimestamp(business.createdAt)}</p>
                            <p>Requested: {formatTimestamp(business.onboarding?.activationRequestedAt)}</p>
                        </div>
                    </div>

                    {business.onboardingStatus === "PENDING_REVIEW" && (
                        <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5">
                            <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
                                <div><h2 className="font-bold text-amber-950">Activation review</h2><p className="mt-1 text-sm text-amber-900">{readyEmployees.length} of {employees.length} employees are payout-ready.</p></div>
                                <span className={`w-fit rounded-full px-3 py-1 text-xs font-bold ${readyEmployees.length ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-700"}`}>{readyEmployees.length ? "READY TO APPROVE" : "NOT READY"}</span>
                            </div>
                            <textarea value={reviewNote} onChange={(event) => setReviewNote(event.target.value)} maxLength={1000} rows={3} placeholder="Note to owner (required for changes or rejection)" className="mt-4 w-full rounded-xl border border-amber-200 bg-white p-3 text-sm" />
                            <div className="mt-3 flex flex-wrap gap-2">
                                <button type="button" onClick={() => handleReview("APPROVE")} disabled={busy || !readyEmployees.length} className="rounded-lg bg-[#0d7c5f] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Approve and publish</button>
                                <button type="button" onClick={() => handleReview("REQUEST_CHANGES")} disabled={busy || !reviewNote.trim()} className="rounded-lg border border-amber-700 px-4 py-2 text-sm font-semibold text-amber-800 disabled:opacity-50">Request changes</button>
                                <button type="button" onClick={() => handleReview("REJECT")} disabled={busy || !reviewNote.trim()} className="rounded-lg border border-red-700 px-4 py-2 text-sm font-semibold text-red-700 disabled:opacity-50">Reject</button>
                            </div>
                        </div>
                    )}
                    {business.onboarding?.reviewNote && business.onboardingStatus !== "PENDING_REVIEW" && <p className="mt-5 rounded-xl bg-gray-50 p-4 text-sm text-gray-700"><strong>Latest review note:</strong> {business.onboarding.reviewNote}</p>}
                    {message && <p className="mt-4 rounded-xl bg-blue-50 p-3 text-sm text-blue-800">{message}</p>}
                </section>

                <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                    <div className="flex items-center justify-between gap-4"><div><h2 className="text-xl font-bold">Employees</h2><p className="mt-1 text-sm text-gray-500">{readyEmployees.length}/{employees.length} payout-ready</p></div><button type="button" onClick={() => setIsModalOpen(true)} className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white">Add draft</button></div>
                    <div className="mt-5 divide-y divide-gray-100">
                        {!employees.length && <p className="py-4 text-sm text-gray-500">No employees.</p>}
                        {employees.map((employee) => {
                            const issue = employee.accountStatus === "deactivated"
                                ? "Employee is deactivated"
                                : employee.accountStatus === "readonly"
                                    ? "Tipping is paused"
                                    : getEmployeeReadinessIssue(employee, business);
                            return <div key={employee.id} className="flex items-center justify-between gap-4 py-3"><div><p className="font-semibold text-gray-900">{employee.displayName || employee.name || "Employee"} <span className="font-normal text-gray-400">#{employee.tipID}</span></p><p className={`mt-1 text-xs ${issue ? "text-amber-700" : "text-emerald-700"}`}>{issue || "Payout-ready"} · {employee.accountStatus}</p></div><Link to={`/admin/business/${businessID}/employee/${employee.id}`} className="text-sm font-semibold text-[#0d7c5f]">Details</Link></div>;
                        })}
                    </div>
                </section>

                <div className="grid gap-6 lg:grid-cols-2">
                    <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm"><h2 className="text-lg font-bold">Recent tips</h2><div className="mt-4 divide-y divide-gray-100">{tips.slice(0, 5).map((tip) => <div key={tip.id} className="flex justify-between gap-4 py-3 text-sm"><div><strong>€{Number(tip.amount || 0).toFixed(2)}</strong><p className="text-gray-500">{tip.feedback || "No feedback"}</p></div><div className="text-right text-gray-500"><p>{employeeName(tip.employeeID)}</p><p className="text-xs">{formatTimestamp(tip.createdAt)}</p></div></div>)}{!tips.length && <p className="text-sm text-gray-500">No tips.</p>}</div></section>
                    <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm"><h2 className="text-lg font-bold">Recent payouts</h2><div className="mt-4 divide-y divide-gray-100">{payouts.slice(0, 5).map((payout) => <div key={payout.id} className="flex justify-between gap-4 py-3 text-sm"><div><strong>€{Number(payout.totalAmount || 0).toFixed(2)}</strong><p className="text-gray-500">{payout.status}</p></div><div className="text-right text-gray-500"><p>{employeeName(payout.employeeID)}</p><p className="text-xs">{formatTimestamp(payout.createdAt)}</p></div></div>)}{!payouts.length && <p className="text-sm text-gray-500">No payouts.</p>}</div></section>
                </div>
            </div>

            {isModalOpen && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"><form onSubmit={handleAddEmployee} className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"><h2 className="text-xl font-bold">Add employee draft</h2><p className="mt-2 text-sm text-gray-500">Only a display name is needed.</p><input autoFocus value={newEmployeeName} onChange={(event) => setNewEmployeeName(event.target.value)} maxLength={100} required className="mt-5 w-full rounded-xl border border-gray-300 p-3" placeholder="Display name" /><div className="mt-5 flex justify-end gap-2"><button type="button" onClick={() => setIsModalOpen(false)} className="rounded-lg bg-gray-100 px-4 py-2">Cancel</button><button disabled={busy} className="rounded-lg bg-[#0d7c5f] px-4 py-2 font-semibold text-white disabled:opacity-50">Create draft</button></div></form></div>}
        </main>
    );
}

export default AdminBusinessDashboard;
